// Las piezas de la ventana «Santi» (propuesta del 03-11, docs/pelotas/ux-2026-11-03/propuesta): quién (S, P, el robot
// del agente), los globos de la conversación, el separador de día, la raya «Nuevo», el punto blanco de lo nuevo y la
// línea que ve un socio en lugar de lo que solo envía Pau. Desde el 04-11, con el lenguaje de Apple de la oficina (ADR
// D-037, docs/design-system.md §Oficina): los globos y las píldoras de día son los de ConversacionOficina. Sin oro aquí:
// el oro es solo «Enviar».
import { Bot } from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Cola } from '../chat/Cola';
import { inicialDe, nombreDe } from './tiempo';

const TAMANO = {
  24: 'h-6 w-6 text-o-nota',
  32: 'h-8 w-8 text-o-texto',
  40: 'h-10 w-10 text-o-titulo3',
} as const;

/** P (Pau), S (Santi): un círculo gris con la inicial. */
export function AvatarPersona({ persona, tamano = 32, className }: { persona: string; tamano?: keyof typeof TAMANO; className?: string }) {
  return (
    <span
      className={cn('relative inline-flex shrink-0 items-center justify-center rounded-full bg-oficina-control font-semibold text-oficina-texto', TAMANO[tamano], className)}
      aria-label={nombreDe(persona)}
      role="img"
    >
      {inicialDe(persona)}
    </span>
  );
}

/** El agente: un robot sobre un cuadrado redondeado (las esquinas del icono de encargo de la oficina). */
export function AvatarAgente({ tamano = 32, className }: { tamano?: 32 | 40; className?: string }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center bg-oficina-control text-oficina-texto', tamano === 40 ? 'h-10 w-10 rounded-o-m' : 'h-8 w-8 rounded-o-s', className)} aria-hidden>
      <Bot className={tamano === 40 ? 'h-[22px] w-[22px]' : 'h-[18px] w-[18px]'} strokeWidth={1.8} />
    </span>
  );
}

/** El punto blanco de «Santi te ha escrito y no lo has visto» (el de toda la oficina para lo nuevo). */
export function PuntoNuevo({ className }: { className?: string }) {
  return <i className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-oficina-texto', className)} aria-label={t('oficinaV2.pelotas.lista.nuevo')} role="img" data-testid="punto-nuevo" />;
}

/** Un enlace dentro de una línea («No, sigo yo», «Verla», «Revisar»): subrayado suave, en gris (el oro es solo «Enviar»).
 *  Con el dedo crece a 44 de alto como todo botón (`index.css`, D-037 «Táctil»). */
export const ENLACE =
  'inline-flex items-center rounded-o-s font-medium text-oficina-texto2 underline decoration-oficina-texto2/45 underline-offset-2 outline-none transition-colors duration-150 ease-o-apple hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-white/85';

/** «112/120»: cuántas letras van de las que caben, solo cerca del límite (desde el 90 %); en rojo al llegar. */
export function Contador({ n, max, className }: { n: number; max: number; className?: string }) {
  if (n < Math.floor(max * 0.9)) return null;
  return (
    <span className={cn('shrink-0 text-o-sub tabular-nums', n >= max ? 'font-semibold text-oficina-error' : 'text-oficina-texto3', className)} aria-live="polite" data-testid="contador">
      {t('oficinaV2.pelotas.contador', { n, max })}
    </span>
  );
}

/** «31 de octubre» / «Ayer» / «Hoy»: la píldora de servicio de la oficina (ConversacionOficina `Servicio`), centrada. */
export function SeparadorDia({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 mt-5 flex justify-center" data-testid="separador-dia">
      <span className="rounded-full bg-black/35 px-3 py-1 text-o-sub font-medium text-white/85">{children}</span>
    </div>
  );
}

/** «Nuevo»: dónde empieza lo que Santi escribió desde la última vez que Pau lo abrió (una raya fina, la palabra a la derecha). */
export function RayaNuevo() {
  return (
    <div className="mt-3 flex items-center gap-3 text-o-sub font-semibold text-oficina-texto before:h-px before:flex-1 before:bg-oficina-texto/[0.22]" data-testid="raya-nuevo">
      {t('oficinaV2.pelotas.ventana.nuevo')}
    </div>
  );
}

/**
 * Un globo de la conversación, el mismo que el de la oficina (ConversacionOficina): Santi a la izquierda (el gris del
 * otro, con su «S» en el último de cada grupo), Pau a la derecha (oro oscuro); el último de cada grupo lleva la cola.
 */
export function Globo({ de, hora: h, children, ultimo = true, className }: { de: 'pau' | 'santi'; hora?: string; children: ReactNode; ultimo?: boolean; className?: string }) {
  const mio = de === 'pau';
  return (
    <div className={cn('flex items-end gap-2', ultimo ? 'mt-2' : 'mt-1', mio ? 'justify-end pl-[20%]' : 'pr-12', className)} data-testid={`globo-${de}`}>
      {!mio ? <span className="w-8 shrink-0">{ultimo ? <AvatarPersona persona="santi" /> : null}</span> : null}
      <div
        className={cn(
          'relative max-w-[min(680px,100%)] rounded-o-l px-3.5 py-2 text-o-cuerpo text-oficina-texto',
          mio ? 'bg-oficina-burbuja-pau' : 'bg-oficina-burbuja-agente',
          ultimo && (mio ? 'rounded-br-o-s' : 'rounded-bl-o-s'),
        )}
      >
        <span className="whitespace-pre-wrap break-words">{children}</span>
        {h ? <span className={cn('float-right ml-3 mt-1.5 text-o-nota tabular-nums', mio ? 'text-oficina-meta-pau' : 'text-oficina-texto3')}>{h}</span> : null}
        {ultimo ? <Cola lado={mio ? 'derecha' : 'izquierda'} de={mio ? 'pau' : 'agente'} className={mio ? 'text-oficina-burbuja-pau' : 'text-oficina-burbuja-agente'} /> : null}
      </div>
    </div>
  );
}

/**
 * 02-11 — Lo que ve un socio en lugar de lo que sale hacia Santi como Pau (la caja «Escribe a Santi…», los botones de una
 * tarjeta). Un socio lo lee todo en la oficina; esto solo lo envía Pau (el servidor contesta 403 SOLO_PAU a cualquier
 * otra sesión). Sustituye a la acción, nunca la enseña desactivada (cursor-ux-mirror §2.1).
 */
export function LoEnviaPau({ className }: { className?: string }) {
  return (
    <p className={cn('flex min-h-11 items-center gap-2 text-o-texto text-oficina-texto2', className)} data-testid="lo-envia-pau">
      {t('oficinaV2.pelotas.loEnviaPau')}
    </p>
  );
}
